// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import TopNav from "../components/TopNav";
import {
  confirmTradeOfferExchange,
  getTradeOfferById,
  submitTradeOfferReview,
  updateTradeOfferMeetup,
  updateTradeOfferStatus,
  cancelTradeOffer,
} from "../api/tradeOffers";
import { useToast } from "../components/ToastProvider";
import { RealtimeEvents } from "../realtime/events";
import { useRealtimeSocket } from "../realtime/useRealtime";
import "../styles/OfferDetails.css";

const QUICK_TAG_GROUPS = [
  {
    label: "Communication",
    tags: [
      "Quick Responder",
      "Clear Communication",
      "Easy to Coordinate With",
      "Flexible Scheduling",
      "Professional",
    ],
  },
  {
    label: "Reliability",
    tags: ["Punctual", "Showed Up as Agreed", "Reliable", "Kept Their Word", "Honest Trader"],
  },
  {
    label: "Condition Accuracy",
    tags: [
      "Card Exactly as Described",
      "Condition Matched Listing",
      "Transparent About Flaws",
      "Accurate Photos",
      "No Surprises",
    ],
  },
  {
    label: "Overall Experience",
    tags: ["Smooth Transaction", "Friendly", "Respectful", "Fair Trade", "Would Trade Again", "Highly Recommended"],
  },
];

const STEP_ICON_MAP = {
  1: "/trade-step-icons/check.svg",
  2: "/trade-step-icons/calendar.svg",
  3: "/trade-step-icons/diamond.svg",
  4: "/trade-step-icons/star.svg",
  completed: "/trade-step-icons/check.svg",
};

function formatStatus(status) {
  return String(status || "unknown")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatDateTime(value) {
  if (!value) return "Not scheduled yet";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not scheduled yet";
  return date.toLocaleString();
}

function padDateTimeSegment(value) {
  return String(value).padStart(2, "0");
}

function toLocalMeetupInputs(value) {
  if (!value) return { date: "", time: "" };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: "", time: "" };
  return {
    date: `${date.getFullYear()}-${padDateTimeSegment(date.getMonth() + 1)}-${padDateTimeSegment(date.getDate())}`,
    time: `${padDateTimeSegment(date.getHours())}:${padDateTimeSegment(date.getMinutes())}`,
  };
}

function combineLocalMeetupDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return "";
  const combined = new Date(`${dateValue}T${timeValue}`);
  if (Number.isNaN(combined.getTime())) return "";
  return combined.toISOString();
}

function mapStepDescription(stepNumber) {
  if (stepNumber === 1) return "Review the incoming offer and decide whether to proceed.";
  if (stepNumber === 2) return "Coordinate a safe public meetup and confirm the details.";
  if (stepNumber === 3) return "Both traders must confirm the exchange before it is completed.";
  if (stepNumber === 4) return "Rate the trade with stars and quick tags to close the flow.";
  return "Track the full offer lifecycle in one place.";
}

export default function OfferDetails() {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const realtime = useRealtimeSocket();
  const { showToast, confirm } = useToast();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState("");
  const [error, setError] = useState("");
  const [details, setDetails] = useState(null);
  const [meetupForm, setMeetupForm] = useState({ location: "", date: "", time: "" });
  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const meetupDateInputRef = useRef(null);
  const meetupTimeInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!offerId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getTradeOfferById(offerId);
      setDetails(data || null);
    } catch (err) {
      setError(String(err?.message || "Failed to load offer details."));
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const meetup = details?.meetup;
    const localInputs = toLocalMeetupInputs(meetup?.dateTime);
    setMeetupForm({
      location: meetup?.location || "",
      date: localInputs.date,
      time: localInputs.time,
    });
  }, [details]);

  useEffect(() => {
    const selfReview = details?.reviews?.self;
    setRating(Number(selfReview?.rating || 0));
    setSelectedTags(Array.isArray(selfReview?.tags) ? selfReview.tags : []);
  }, [details]);

  useEffect(() => {
    if (!offerId) return;

    const refreshIfRelevant = (payload) => {
      if (String(payload?.offerId || "") !== String(offerId)) return;
      void load();
    };

    const refreshOnConnect = () => {
      void load();
    };

    realtime.on(RealtimeEvents.TradeOfferStatusUpdated, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeLifecycleUpdated, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeMeetupUpdated, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeExchangeConfirmationUpdated, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeCompleted, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeReviewSubmitted, refreshIfRelevant);
    realtime.on(RealtimeEvents.TradeReviewAutoFinalized, refreshIfRelevant);
    realtime.on("connect", refreshOnConnect);

    return () => {
      realtime.off(RealtimeEvents.TradeOfferStatusUpdated, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeLifecycleUpdated, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeMeetupUpdated, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeExchangeConfirmationUpdated, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeCompleted, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeReviewSubmitted, refreshIfRelevant);
      realtime.off(RealtimeEvents.TradeReviewAutoFinalized, refreshIfRelevant);
      realtime.off("connect", refreshOnConnect);
    };
  }, [offerId, realtime, load]);

  const offer = details?.offer || {};
  const source = details?.source || {};
  const workflow = details?.workflow || {};
  const meetup = details?.meetup || null;
  const completion = details?.completion || null;
  const reviewState = details?.reviews || {};
  const trader = details?.trader || null;
  const status = String(offer?.status || "").toLowerCase();
  const stepDescription = mapStepDescription(Number(workflow?.currentStep || 1));
  const steps = Array.isArray(workflow?.steps) && workflow.steps.length > 0
    ? workflow.steps
    : [
        { number: 1, label: "Review Offer", active: true, completed: false },
        { number: 2, label: "Coordinate Meetup", active: false, completed: false },
        { number: 3, label: "Confirm Exchange", active: false, completed: false },
        { number: 4, label: "Rate & Close", active: false, completed: false },
      ];

  const allowedActions = workflow?.allowedActions || {};
  const canShowMeetup = status === "accepted" || Boolean(completion?.completedAt);
  const canShowExchange = canShowMeetup;
  const canShowReview = Boolean(completion?.completedAt);
  const highlightedStepNumber =
    steps.find((step) => step.active)?.number ||
    (workflow?.isClosed ? [...steps].reverse().find((step) => step.completed)?.number : null);

  const sourceHeading = useMemo(() => {
    if (source?.type === "bounty") return "Original Bounty";
    if (source?.type === "trade") return "Original Trade";
    return "Original Listing";
  }, [source?.type]);

  const handleStatusChange = async (nextStatus) => {
    if (!offer?.id || submitting) return;
    setSubmitting(nextStatus);
    try {
      await updateTradeOfferStatus(offer.id, nextStatus);
      showToast(nextStatus === "accepted" ? "Offer accepted." : "Offer declined.", "success");
      await load();
    } catch (err) {
      showToast(String(err?.message || "Failed to update offer status."), "error");
    } finally {
      setSubmitting("");
    }
  };

  const handleCancel = async () => {
    if (!offer?.id || submitting) return;
    const confirmed = await confirm({
      title: "Withdraw offer?",
      message: "This cancels your offer for both traders. This can't be undone.",
      confirmLabel: "Withdraw",
      tone: "danger",
    });
    if (!confirmed) return;
    setSubmitting("cancel");
    try {
      await cancelTradeOffer(offer.id);
      showToast("Offer withdrawn.", "success");
      await load();
    } catch (err) {
      showToast(String(err?.message || "Failed to withdraw offer."), "error");
    } finally {
      setSubmitting("");
    }
  };

  const handleMeetupSave = async () => {
    if (!offer?.id || submitting) return;
    const combinedDateTime = combineLocalMeetupDateTime(meetupForm.date, meetupForm.time);
    if (!meetupForm.location.trim() || !combinedDateTime) {
      showToast("Please set both location and date/time.", "warning");
      return;
    }
    setSubmitting("meetup");
    try {
      await updateTradeOfferMeetup(offer.id, {
        location: meetupForm.location.trim(),
        dateTime: combinedDateTime,
        confirm: true,
      });
      await load();
      showToast("Meetup saved and confirmed. Waiting for the other trader.", "success");
    } catch (err) {
      showToast(String(err?.message || "Failed to save meetup details."), "error");
    } finally {
      setSubmitting("");
    }
  };

  const handleExchangeConfirm = async () => {
    if (!offer?.id || submitting) return;
    setSubmitting("exchange-confirm");
    try {
      const response = await confirmTradeOfferExchange(offer.id);
      const waiting = Boolean(response?.completion?.waitingForOtherTrader);
      await load();
      showToast(
        waiting ? "Waiting for the other trader to confirm." : "Trade completion recorded.",
        waiting ? "info" : "success",
      );
    } catch (err) {
      showToast(String(err?.message || "Failed to confirm exchange."), "error");
    } finally {
      setSubmitting("");
    }
  };

  const handleTagToggle = (tag) => {
    setSelectedTags((prev) => {
      const exists = prev.includes(tag);
      if (exists) return prev.filter((item) => item !== tag);
      if (prev.length >= 5) {
        showToast("You can select up to 5 tags.", "warning");
        return prev;
      }
      return [...prev, tag];
    });
  };

  const handleReviewSubmit = async () => {
    if (!offer?.id || submitting) return;
    if (!rating) {
      showToast("Please choose a star rating first.", "warning");
      return;
    }
    setSubmitting("review");
    try {
      await submitTradeOfferReview(offer.id, {
        rating,
        tags: selectedTags,
      });
      await load();
      showToast("Review submitted.", "success");
    } catch (err) {
      showToast(String(err?.message || "Failed to submit review."), "error");
    } finally {
      setSubmitting("");
    }
  };

  const renderStars = (interactive = false) => (
    <div className={`offer-rating-stars ${interactive ? "interactive" : ""}`}>
      {[1, 2, 3, 4, 5].map((value) => (
        <button
          key={value}
          type="button"
          className={`star-btn ${value <= Number(rating || reviewState?.self?.rating || 0) ? "filled" : ""}`}
          onClick={interactive ? () => setRating(value) : undefined}
          disabled={!interactive || !allowedActions?.canSubmitReview}
        >
          ★
        </button>
      ))}
    </div>
  );

  const openNativePicker = (inputRef) => {
    const element = inputRef?.current;
    if (!element) return;
    if (typeof element.showPicker === "function") {
      element.showPicker();
      return;
    }
    element.focus();
    element.click?.();
  };

  return (
    <>
      <TopNav />
      <div className="offer-details-page page-with-top-nav">
        <header className="offer-details-header">
          <button type="button" className="back-btn" onClick={() => navigate(-1)}>
            ←
          </button>
          <div>
            <h1>Offer Details</h1>
            <p>{stepDescription}</p>
          </div>
        </header>

        {loading && <div className="offer-details-state">Loading offer details...</div>}
        {!loading && error && <div className="offer-details-state error">{error}</div>}

        {!loading && !error && details && (
          <div className="offer-flow-shell">
            <section className="offer-progress-card">
              <div className="progress-copy">
                <h2>Trade Confirmation Flow</h2>
                <p>{workflow?.statusMessage || "Everything for this offer now lives on one guided screen."}</p>
              </div>
              <div className="progress-rail" role="list" aria-label="Trade confirmation progress">
                <span className="progress-track" aria-hidden="true" />
                {steps.map((step, index) => (
                  <div
                    key={step.number}
                    className={`progress-step ${step.active ? "active" : ""} ${step.completed ? "completed" : ""} ${highlightedStepNumber === step.number ? "spotlight" : ""}`}
                    role="listitem"
                  >
                    {index > 0 && (
                      <span
                        className={`progress-line before ${steps[index - 1]?.completed ? "filled" : ""}`}
                        aria-hidden="true"
                      />
                    )}
                    {index < steps.length - 1 && (
                      <span
                        className={`progress-line after ${step.completed ? "filled" : ""}`}
                        aria-hidden="true"
                      />
                    )}
                    <div className="progress-node-shell">
                      <span
                        className="progress-node-icon"
                        style={{
                          "--step-icon": `url(${step.completed ? STEP_ICON_MAP.completed : STEP_ICON_MAP[step.number]})`,
                        }}
                        aria-hidden="true"
                      />
                    </div>
                    <span className="step-label">{step.label}</span>
                  </div>
                ))}
              </div>
            </section>

            <section className="offer-stage-card">
              <div className="stage-heading">
                <div>
                  <p className="stage-kicker">Step 1</p>
                  <h3>Review Offer</h3>
                </div>
                <span className={`status-badge status-${status || "unknown"}`}>{formatStatus(status || "unknown")}</span>
              </div>

              <div className="offer-review-grid">
                <section className="offer-card">
                  <div className="card-head">
                    <h2>{sourceHeading}</h2>
                  </div>
                  {source?.photoUrl ? (
                    <img className="source-image" src={source.photoUrl} alt={source.title || "Source card"} />
                  ) : (
                    <div className="source-image-placeholder">No image</div>
                  )}
                  <div className="details-grid">
                    <div>
                      <span className="label">Card</span>
                      <span className="value">{source?.title || "N/A"}</span>
                    </div>
                    <div>
                      <span className="label">Game</span>
                      <span className="value">{source?.game || "N/A"}</span>
                    </div>
                    <div>
                      <span className="label">Condition</span>
                      <span className="value">{source?.condition || "N/A"}</span>
                    </div>
                    <div>
                      <span className="label">Grade</span>
                      <span className="value">
                        {source?.gradingCompany && source?.gradingCompany !== "raw"
                          ? `${String(source.gradingCompany).toUpperCase()} ${source?.gradeNumber || ""}`.trim()
                          : "Raw / N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="label">Location</span>
                      <span className="value">
                        {source?.zipCode ? `ZIP ${source.zipCode}` : "N/A"}
                        {source?.radius ? ` (${source.radius} mi)` : ""}
                      </span>
                    </div>
                    <div>
                      <span className="label">Type</span>
                      <span className="value">{String(source?.type || "offer").toUpperCase()}</span>
                    </div>
                  </div>
                </section>

                <section className="offer-card">
                  <div className="card-head">
                    <h2>{details?.viewerRole === "recipient" ? "Fulfillment Offer" : "Your Submitted Offer"}</h2>
                  </div>
                  {offer?.offerPhotoUrl ? (
                    <img className="source-image" src={offer.offerPhotoUrl} alt={offer.offerCardName || "Offered card"} />
                  ) : (
                    <div className="source-image-placeholder">No image</div>
                  )}
                  <div className="details-grid">
                    <div>
                      <span className="label">Offered Card</span>
                      <span className="value">{offer?.offerCardName || "N/A"}</span>
                    </div>
                    <div>
                      <span className="label">Condition</span>
                      <span className="value">{offer?.offerCardCondition || "N/A"}</span>
                    </div>
                    <div>
                      <span className="label">Grade</span>
                      <span className="value">
                        {offer?.offerGradingCompany && offer?.offerGradingCompany !== "raw"
                          ? `${String(offer.offerGradingCompany).toUpperCase()} ${offer?.offerGradeNumber || ""}`.trim()
                          : "Raw / N/A"}
                      </span>
                    </div>
                    <div>
                      <span className="label">Submitted</span>
                      <span className="value">{offer?.createdAt ? new Date(offer.createdAt).toLocaleString() : "N/A"}</span>
                    </div>
                  </div>

                  <div className="message-panel">
                    <label>Message</label>
                    <textarea
                      readOnly
                      value={offer?.message || `I sent you a trade offer for ${offer?.offerCardName || "this card"}.`}
                    />
                  </div>

                  <div className="trader-meta">
                    <div>
                      <span className="label">Trading With</span>
                      <span className="value">{trader?.name || "Cardex Trader"}</span>
                    </div>
                    <div>
                      <span className="label">Trust</span>
                      <span className="value">
                        {trader?.trustScore || "Neutral"} · {Number(trader?.reviewCount || 0)} reviews
                      </span>
                    </div>
                    <div>
                      <span className="label">Completed Trades</span>
                      <span className="value">{Number(trader?.completedTrades || 0)}</span>
                    </div>
                  </div>

                  <div className="actions-row">
                    {allowedActions?.canAccept && (
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={Boolean(submitting)}
                        onClick={() => handleStatusChange("accepted")}
                      >
                        {submitting === "accepted" ? "Accepting..." : "Accept"}
                      </button>
                    )}
                    {allowedActions?.canDecline && (
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={Boolean(submitting)}
                        onClick={() => handleStatusChange("rejected")}
                      >
                        {submitting === "rejected" ? "Declining..." : "Decline"}
                      </button>
                    )}
                    {allowedActions?.canCancel && (
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={Boolean(submitting)}
                        onClick={handleCancel}
                      >
                        {submitting === "cancel" ? "Withdrawing..." : "Withdraw offer"}
                      </button>
                    )}
                    {!allowedActions?.canAccept && !allowedActions?.canDecline && !allowedActions?.canCancel && (
                      <div className="read-only-note">
                        {workflow?.statusMessage || `This offer is ${formatStatus(status || "unknown")}.`}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </section>

            {canShowMeetup && (
              <section className={`offer-stage-card ${Number(workflow?.currentStep || 0) === 2 ? "active-stage" : ""}`}>
                <div className="stage-heading">
                  <div>
                    <p className="stage-kicker">Step 2</p>
                    <h3>Coordinate Meetup</h3>
                  </div>
                  <span className={`mini-stage-state ${meetup?.status || "pending"}`}>{formatStatus(meetup?.status || "pending")}</span>
                </div>

                <div className="meetup-layout">
                  <div className="meetup-form-card">
                    <label>
                      Location
                      <input
                        type="text"
                        value={meetupForm.location}
                        onChange={(event) => setMeetupForm((prev) => ({ ...prev, location: event.target.value }))}
                        placeholder="Choose a safe public location"
                        disabled={!allowedActions?.canEditMeetup}
                      />
                    </label>

                    <div className="meetup-picker-grid">
                      <label>
                        Meetup Date
                        <div className="picker-input-wrap">
                          <input
                            ref={meetupDateInputRef}
                            type="date"
                            value={meetupForm.date}
                            onChange={(event) => setMeetupForm((prev) => ({ ...prev, date: event.target.value }))}
                            disabled={!allowedActions?.canEditMeetup}
                          />
                          <button
                            type="button"
                            className="picker-trigger"
                            onClick={() => openNativePicker(meetupDateInputRef)}
                            disabled={!allowedActions?.canEditMeetup}
                          >
                            Pick
                          </button>
                        </div>
                      </label>

                      <label>
                        Meetup Time
                        <div className="picker-input-wrap">
                          <input
                            ref={meetupTimeInputRef}
                            type="time"
                            value={meetupForm.time}
                            onChange={(event) => setMeetupForm((prev) => ({ ...prev, time: event.target.value }))}
                            disabled={!allowedActions?.canEditMeetup}
                          />
                          <button
                            type="button"
                            className="picker-trigger"
                            onClick={() => openNativePicker(meetupTimeInputRef)}
                            disabled={!allowedActions?.canEditMeetup}
                          >
                            Pick
                          </button>
                        </div>
                      </label>
                    </div>

                    <div className="meetup-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={!allowedActions?.canEditMeetup || Boolean(submitting)}
                        onClick={handleMeetupSave}
                      >
                        {submitting === "meetup" ? "Saving..." : "Save & Confirm Meetup"}
                      </button>
                      <button
                        type="button"
                        className="btn-secondary"
                        disabled={!details?.conversationId}
                        onClick={() => navigate(`/messages/${details?.conversationId}`)}
                      >
                        Open Trade Chat
                      </button>
                    </div>
                  </div>

                  <div className="meetup-side-card">
                    <div className="meetup-summary-item">
                      <span className="label">Current Plan</span>
                      <span className="value">{meetup?.location || "No location selected yet"}</span>
                    </div>
                    <div className="meetup-summary-item">
                      <span className="label">When</span>
                      <span className="value">{formatDateTime(meetup?.dateTime)}</span>
                    </div>
                    <div className="meetup-summary-item">
                      <span className="label">Meetup Confirmed</span>
                      <span className="value">{meetup?.viewerConfirmed ? "You confirmed" : "Awaiting your confirmation"}</span>
                    </div>

                    <div className="safety-card">
                      <h4>Safety Reminder</h4>
                      <ul>
                        <li>Meet in a public place like a card shop, cafe, or library.</li>
                        <li>Inspect cards carefully before finalizing the swap.</li>
                        <li>Use trade chat to keep the full meetup record in one place.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {canShowExchange && (
              <section className={`offer-stage-card ${Number(workflow?.currentStep || 0) === 3 ? "active-stage" : ""}`}>
                <div className="stage-heading">
                  <div>
                    <p className="stage-kicker">Step 3</p>
                    <h3>Confirm Exchange</h3>
                  </div>
                </div>

                <div className="exchange-card">
                  <div className="exchange-summary">
                    <div>
                      <span className="label">Your Confirmation</span>
                      <span className="value">
                        {completion?.viewerConfirmedAt ? `Confirmed ${formatDateTime(completion.viewerConfirmedAt)}` : "Not confirmed yet"}
                      </span>
                    </div>
                    <div>
                      <span className="label">Other Trader</span>
                      <span className="value">
                        {completion?.otherConfirmedAt ? `Confirmed ${formatDateTime(completion.otherConfirmedAt)}` : "Waiting for them"}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="btn-sparkle"
                    disabled={!allowedActions?.canConfirmExchange || Boolean(submitting)}
                    onClick={handleExchangeConfirm}
                  >
                    {submitting === "exchange-confirm" ? "Confirming..." : "Mark Trade as Completed"}
                  </button>

                  <p className="exchange-note">
                    {completion?.waitingForOtherTrader
                      ? "Waiting for the other trader to confirm."
                      : "Both users must press this button before the trade moves forward."}
                  </p>
                </div>
              </section>
            )}

            {canShowReview && (
              <section className={`offer-stage-card ${Number(workflow?.currentStep || 0) === 4 ? "active-stage" : ""}`}>
                <div className="stage-heading">
                  <div>
                    <p className="stage-kicker">Step 4</p>
                    <h3>Rate & Close</h3>
                  </div>
                  <span className="completion-pill">Trade Completed</span>
                </div>

                <div className="review-layout">
                  <div className="review-summary-card">
                    <h4>Trade Completed</h4>
                    <p>
                      {source?.title || "Trade"} exchanged for {offer?.offerCardName || "your offered card"}.
                    </p>
                    <div className="review-summary-meta">
                      <span>Completed: {formatDateTime(completion?.completedAt)}</span>
                      {reviewState?.autoReviewDueAt && !reviewState?.allResolved && (
                        <span>Auto-finalizes: {formatDateTime(reviewState.autoReviewDueAt)}</span>
                      )}
                    </div>

                    {reviewState?.counterparty && (
                      <div className="counterparty-review">
                        <h5>Counterparty Rating Submitted</h5>
                        <div className="counterparty-rating">
                          {"★".repeat(Number(reviewState.counterparty.rating || 0))}
                          {"☆".repeat(Math.max(0, 5 - Number(reviewState.counterparty.rating || 0)))}
                        </div>
                        {reviewState?.counterparty?.autoGenerated && (
                          <span className="auto-badge">Auto-finalized after 48h</span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="review-form-card">
                    <h4>Your Rating</h4>
                    {renderStars(Boolean(allowedActions?.canSubmitReview))}

                    <div className="tag-groups">
                      {QUICK_TAG_GROUPS.map((group) => (
                        <div key={group.label} className="tag-group">
                          <div className="tag-group-label">{group.label}</div>
                          <div className="tag-options">
                            {group.tags.map((tag) => {
                              const selected = selectedTags.includes(tag);
                              return (
                                <button
                                  key={tag}
                                  type="button"
                                  className={`tag-chip ${selected ? "selected" : ""}`}
                                  disabled={!allowedActions?.canSubmitReview}
                                  onClick={() => handleTagToggle(tag)}
                                >
                                  {tag}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="review-actions">
                      <button
                        type="button"
                        className="btn-primary"
                        disabled={!allowedActions?.canSubmitReview || Boolean(submitting)}
                        onClick={handleReviewSubmit}
                      >
                        {submitting === "review" ? "Submitting..." : reviewState?.self ? "Review Submitted" : "Submit Rating"}
                      </button>
                      {!allowedActions?.canSubmitReview && reviewState?.self && (
                        <div className="read-only-note">
                          Your rating is locked. {reviewState?.self?.autoGenerated ? "It was auto-finalized after 48 hours." : "Thanks for closing out the trade."}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
}
